import { useState } from "react";
import { formatValue, type Point, type Widget } from "../api";

const W = 900;
const H = 260;
const PAD = { top: 18, right: 18, bottom: 30, left: 66 };

// One calm colour per series. A chart with eight loud lines is harder to read
// than one with two clear ones.
const SERIES_COLORS = ["#2563eb", "#16a34a", "#7c3aed", "#d97706"];

/** Standard "nice number" rounding, so axis labels are 0 / 50 / 100, not 47.3. */
function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range || 1));
  const f = (range || 1) / 10 ** exp;
  const nf = round
    ? f < 1.5
      ? 1
      : f < 3
        ? 2
        : f < 7
          ? 5
          : 10
    : f <= 1
      ? 1
      : f <= 2
        ? 2
        : f <= 5
          ? 5
          : 10;
  return nf * 10 ** exp;
}

function niceScale(min: number, max: number): { min: number; max: number; ticks: number[] } {
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const step = niceNum((max - min) / 2, true);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = niceMin; t <= niceMax + step * 0.5; t += step) ticks.push(t);
  return { min: niceMin, max: niceMax, ticks };
}

/**
 * A clean multi-series line chart.
 *
 * Two deliberate choices keep it readable for anyone:
 * - No area fill and no clutter — just labelled lines.
 * - Outlier-proof scale. A single artifact day (e.g. a −1200% margin when heavy
 *   returns leave revenue near zero) would otherwise flatten every real value
 *   into a line at the top. For percent charts the axis is built from the sane
 *   range and a freak point is clamped to the edge and drawn red, so the normal
 *   variation stays visible and the bad day still stands out.
 */
export function LineChart({
  widget,
  onSelect,
}: {
  widget: Widget;
  onSelect?: (periodStart: string, label: string) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const series = Object.entries(widget.series).filter(([, pts]) => pts.length > 0);
  if (series.length === 0) return <EmptyPanel widget={widget} />;

  const axis: Point[] = series.reduce(
    (longest, [, pts]) => (pts.length > longest.length ? pts : longest),
    [] as Point[],
  );

  const isPercent = widget.format === "percent";
  const allValues = series.flatMap(([, pts]) =>
    pts.map((p) => p.value).filter((v): v is number => v !== null),
  );

  // Build the scale from the "sane" values so one freak point cannot dominate.
  const scaleBasis = isPercent
    ? allValues.filter((v) => v >= -100 && v <= 200)
    : allValues;
  const basis = scaleBasis.length ? scaleBasis : allValues;
  let lo = Math.min(...basis);
  let hi = Math.max(...basis);
  if (!isPercent) {
    // Money and counts read most honestly from a zero baseline.
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  const scale = niceScale(lo, hi);

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const span = scale.max - scale.min || 1;
  const x = (i: number) =>
    PAD.left + (axis.length === 1 ? innerW / 2 : (i / (axis.length - 1)) * innerW);
  const clampY = (v: number) => Math.min(scale.max, Math.max(scale.min, v));
  const y = (v: number) => PAD.top + innerH - ((clampY(v) - scale.min) / span) * innerH;
  const isOff = (v: number) => v < scale.min || v > scale.max;

  const pathFor = (pts: Point[]) => {
    const segments: string[] = [];
    let cur: string[] = [];
    pts.forEach((p, i) => {
      if (p.value === null) {
        if (cur.length) segments.push(cur.join(" "));
        cur = [];
        return;
      }
      cur.push(`${cur.length === 0 ? "M" : "L"} ${x(i)} ${y(p.value)}`);
    });
    if (cur.length) segments.push(cur.join(" "));
    return segments;
  };

  const labelStep = Math.max(1, Math.ceil(axis.length / 7));

  return (
    <div className="panel">
      <div className="panel-header row">
        <div>
          <h2>{widget.title}</h2>
          {widget.subtitle && <p>{widget.subtitle}</p>}
        </div>
        {series.length > 1 && (
          <div className="legend">
            {series.map(([name], s) => (
              <span className="legend-item" key={name}>
                <span
                  className="legend-swatch"
                  style={{ background: SERIES_COLORS[s % SERIES_COLORS.length] }}
                />
                {humanize(name)}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label={widget.title}>
        {scale.ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className="grid" />
            <text x={PAD.left - 10} y={y(t) + 4} className="axis-label" textAnchor="end">
              {formatValue(t, widget.format, "axis")}
            </text>
          </g>
        ))}

        {series.map(([name, pts], s) =>
          pathFor(pts).map((d, i) => (
            <path
              key={`${name}-${i}`}
              d={d}
              className="line"
              style={{ stroke: SERIES_COLORS[s % SERIES_COLORS.length] }}
            />
          )),
        )}

        {/* Markers on the primary series; a clamped (off-scale) point is red. */}
        {series[0][1].map((p, i) =>
          p.value === null ? null : (
            <circle
              key={i}
              cx={x(i)}
              cy={y(p.value)}
              r={hover === i ? 6 : 3.5}
              className={`dot${hover === i ? " active" : ""}${isOff(p.value) ? " off" : ""}`}
              style={isOff(p.value) ? undefined : { stroke: SERIES_COLORS[0] }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ),
        )}
        {onSelect &&
          axis.map((p, i) =>
            p.period_start ? (
              <rect
                key={`hit${i}`}
                x={x(i) - innerW / Math.max(axis.length - 1, 1) / 2}
                y={PAD.top}
                width={innerW / Math.max(axis.length - 1, 1)}
                height={innerH}
                fill="transparent"
                className="hit"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect(p.period_start!, p.label)}
              />
            ) : null,
          )}

        {axis.map((p, i) =>
          i % labelStep === 0 ? (
            <text key={`l${i}`} x={x(i)} y={H - 8} className="axis-label" textAnchor="middle">
              {p.label}
            </text>
          ) : null,
        )}
      </svg>

      {hover !== null && (
        <div className="tooltip">
          <strong>{axis[hover]?.label}</strong>
          {series.map(([name, pts], s) => (
            <span key={name}>
              <span
                className="legend-swatch"
                style={{ background: SERIES_COLORS[s % SERIES_COLORS.length] }}
              />
              {formatValue(pts[hover]?.value ?? null, widget.format)}
            </span>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

export function PanelHeader({ widget }: { widget: Widget }) {
  return (
    <div className="panel-header">
      <h2>{widget.title}</h2>
      {widget.subtitle && <p>{widget.subtitle}</p>}
    </div>
  );
}

export function EmptyPanel({ widget }: { widget: Widget }) {
  return (
    <div className="panel">
      <PanelHeader widget={widget} />
      <div className="empty">No data in range</div>
    </div>
  );
}

export function humanize(name: string): string {
  return name.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}
